import { useState } from "react";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Mail, MessageSquare, CheckCircle2 } from "lucide-react";

const empty = { name: "", email: "", phone: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success("Message sent.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send message.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicLayout>
      <div className="mx-auto max-w-7xl px-6 py-20 md:py-28 lg:px-8" data-testid="contact-page">
        <div className="grid gap-12 md:grid-cols-2">
          <div>
            <p className="font-heading text-xs font-semibold uppercase tracking-[0.25em] text-primary">
              Contact
            </p>
            <h1 className="mt-3 font-display text-5xl uppercase leading-none tracking-wide text-white sm:text-6xl">
              Get in touch
            </h1>
            <p className="mt-6 font-body leading-relaxed text-zinc-400">
              Have a question about a program or an enrollment? Send us a message
              using the form and we'll get back to you.
            </p>
            <div className="mt-8 space-y-4">
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-sm bg-primary/15 text-primary">
                  <MessageSquare className="h-5 w-5" />
                </span>
                <div>
                  <p className="font-heading font-medium text-white">Form-only inquiries</p>
                  <p className="font-body text-sm text-zinc-500">We respond to every message we receive.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-sm bg-primary/15 text-primary">
                  <Mail className="h-5 w-5" />
                </span>
                <div>
                  <p className="font-heading font-medium text-white">Kept private</p>
                  <p className="font-body text-sm text-zinc-500">Your details are stored securely and never sold.</p>
                </div>
              </div>
            </div>
          </div>

          <div>
            {done ? (
              <div className="rounded-sm border border-border bg-card p-10 text-center" data-testid="contact-success">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/15 text-primary">
                  <CheckCircle2 className="h-8 w-8" />
                </span>
                <h2 className="mt-6 font-heading text-2xl font-bold tracking-tight text-white">Message sent</h2>
                <p className="mt-3 font-body text-zinc-400">Thanks for reaching out. We'll be in touch soon.</p>
                <Button variant="outline" className="mt-6 rounded-sm border-border bg-transparent text-white hover:border-primary hover:bg-secondary" onClick={() => { setForm(empty); setDone(false); }} data-testid="contact-reset-button">
                  Send another message
                </Button>
              </div>
            ) : (
              <form onSubmit={submit} className="rounded-sm border border-border bg-card p-6 md:p-8" data-testid="contact-form">
                <div className="space-y-5">
                  <div>
                    <Label htmlFor="c-name" className="mb-2 block font-heading text-sm font-semibold uppercase tracking-wide text-zinc-300">Name</Label>
                    <Input id="c-name" data-testid="contact-input-name" required value={form.name} onChange={set("name")} placeholder="Your name" />
                  </div>
                  <div>
                    <Label htmlFor="c-email" className="mb-2 block font-heading text-sm font-semibold uppercase tracking-wide text-zinc-300">Email</Label>
                    <Input id="c-email" data-testid="contact-input-email" required type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" />
                  </div>
                  <div>
                    <Label htmlFor="c-phone" className="mb-2 block font-heading text-sm font-semibold uppercase tracking-wide text-zinc-300">Phone (optional)</Label>
                    <Input id="c-phone" data-testid="contact-input-phone" value={form.phone} onChange={set("phone")} placeholder="(555) 123-4567" />
                  </div>
                  <div>
                    <Label htmlFor="c-message" className="mb-2 block font-heading text-sm font-semibold uppercase tracking-wide text-zinc-300">Message</Label>
                    <Textarea id="c-message" data-testid="contact-input-message" required rows={5} value={form.message} onChange={set("message")} placeholder="How can we help?" />
                  </div>
                  <Button type="submit" data-testid="contact-submit-button" disabled={submitting} className="w-full rounded-sm bg-primary font-heading font-bold uppercase tracking-wider text-primary-foreground hover:bg-orange-500">
                    {submitting ? "Sending…" : "Send Message"}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
